import { ConfirmDialog } from '@/components/confirm-dialog';
import { companiesApi } from '@/lib/api';
import type { Company } from '@mss_creator/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export const ARCHIVE_COMPANY_ERROR_MESSAGE = "L'archivage a échoué. Réessayez.";

export interface ArchiveCompanyDialogProps {
  /** The company to archive; `null` keeps the dialog closed. */
  company: Pick<Company, 'id' | 'name'> | null;
  onClose: () => void;
  /** Called once the company is archived, before the dialog closes — for a page whose list is not a query. */
  onArchived?: () => void;
}

/**
 * Confirmation before archiving a company (issue #20, planche 02). Archiving
 * can be undone from the archives, so « Archiver » is the solid blue-night
 * button rather than the red one. Escape and « Annuler » close; while the call
 * runs the dialog cannot be dismissed; on failure it stays open with the
 * error, and « Archiver » retries.
 */
export function ArchiveCompanyDialog({ company, onClose, onArchived }: ArchiveCompanyDialogProps) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) => companiesApi.archive(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['companies'] });
      onArchived?.();
      onClose();
    },
  });

  function handleCancel() {
    mutation.reset();
    onClose();
  }

  return (
    <ConfirmDialog
      open={company !== null}
      title={company ? `Archiver « ${company.name} » ?` : ''}
      description="L'entreprise et ses matrices ne seront plus accessibles. Vous pourrez la restaurer depuis les archives."
      confirmLabel="Archiver"
      pendingLabel="Archivage…"
      confirmIcon="archive"
      confirmVariant="default"
      isPending={mutation.isPending}
      errorMessage={mutation.isError ? ARCHIVE_COMPANY_ERROR_MESSAGE : null}
      onConfirm={() => {
        if (company) mutation.mutate(company.id);
      }}
      onCancel={handleCancel}
    />
  );
}
